<template>
    <div class="history">
        <b-field class="datetime-field" :label="$t('dt_picker.date_from')">
            <b-datetimepicker
                :max-datetime="maxDate"
                :datetime-formatter="datetimeFormatter"
                locale="en-GB"
                :mobile-native="false"
                v-model="dateStart"
                ref="dtFrom"
                :focusOnShow="false"
                editable>
                <template slot="left">
                    <button class="button" @click="$refs.dtFrom.toggle()">
                        <span>{{ $t('dt_picker.cancel') }}</span>
                    </button>
                </template>
                <template slot="right">
                    <button class="button" @click="$refs.dtFrom.toggle()">
                        <span>{{ $t('dt_picker.confirm') }}</span>
                    </button>
                </template>
            </b-datetimepicker>
        </b-field>
        <b-field class="datetime-field" :label="$t('dt_picker.date_to')">
            <b-datetimepicker
                :max-datetime="maxDate"
                :datetime-formatter="datetimeFormatter"
                locale="en-GB"
                :mobile-native="false"
                v-model="dateEnd"
                ref="dtTo"
                :focusOnShow="false"
                editable>
                <template slot="left">
                    <button class="button" @click="$refs.dtTo.toggle()">
                        <span>{{ $t('dt_picker.cancel') }}</span>
                    </button>
                </template>
                <template slot="right">
                    <button class="button" @click="$refs.dtTo.toggle()">
                        <span>{{ $t('dt_picker.confirm') }}</span>
                    </button>
                </template>
            </b-datetimepicker>
        </b-field>
        <b-message type="is-danger" v-if="errorMessage">
            {{ errorMessage }}
        </b-message>

        <div class="dt-control-row">
            <div>
                <a role="button" class="pagination-previous" @click="datetimeAddDays(-1)">
                    <span class="icon has-text-primary is-clickable">
                        <i class="fas fa-angle-left"></i>
                    </span>
                </a>
            </div>
            <b-button :loading="isLoading" @click="btnGetDeviceHistory">{{ $t('history.get_history') }}</b-button>
            <div>
                <a role="button" class="pagination-next" @click="datetimeAddDays(1)" :disabled="dateNextDisabled">
                    <span class="icon has-text-primary is-clickable">
                    <i class="fas fa-angle-right"></i>
                    </span>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'History',
    data() {
        return {
            isLoading: true,
            errorMessage: null,
            maxDate: new Date(),
            maxDateMoment: this.$moment(),
            dateStart: null,
        };
    },
    methods: {
        clearMessages() {
            this.errorMessage = null;
        },
        clearState() {
            this.isLoading = false;
            let now = this.$moment();
            this.dateStart = now.startOf('day').toDate();
            this.dateEnd = now.endOf('day').toDate();
        },
        datetimeAddDays(days) {
            if (days) {
                let newEnd = this.$moment(this.dateEnd).add(days, 'days');

                if (newEnd.isSameOrBefore(this.$moment(), 'days')) {
                    this.dateStart = this.$moment(this.dateStart).add(days, 'days').toDate();
                    this.dateEnd = newEnd.toDate();
                    this.getDeviceHistory();
                }
            }
        },
        btnGetDeviceHistory() {
            // Use separate function to call getDeviceHistory to know when the button was pressed.
            // Close/hide history window to show full height map.
            this.getDeviceHistory(true);
        },
        getDeviceHistory(hidePanel) {
            this.$store.dispatch('map/clearLayerGroupLayers', 'markers');
            this.isLoading = true;

            this.clearMessages();

            this.clearDeviceHistory();
            let start = this.$moment(this.dateStart);
            let end = this.$moment(this.dateEnd);

            // Limit only 7 days to the history range
            if (end.diff(start, 'days') > 7) {
                this.errorMessage = this.$t('history.date_range_error');
                this.isLoading = false;
                return;
            }

            let params = {
                from: start.format(),
                to: end.format(),
                column: ['lat', 'lon', 'time', 'distance', 'speed', 'alt']
            };
            this.$store.state.api.fetchDeviceHistory(this.$store.getters.selectedDevice.id, params)
                .then(data => {
                    if (data.sessions && data.sessions.length) {
                        if (hidePanel) {
                            this.$store.dispatch('deviceDetailsPanelMinimized', true);
                        }
                        this.$store.dispatch('map/setHistorySessions', [data.sessions, data.columns, start, end]);
                    } else {
                        this.$toastSuccess(this.$t('history.no_data_for_period'));
                    }
                })
                .catch(() => {
                    this.$toastGenericError();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        clearDeviceHistory() {
            this.$store.dispatch('map/clearHistorySessions');
        },
        datetimeFormatter(dt) {
            return this.$moment(dt).format('DD/MM/YYYY HH:mm');
        }
    },
    computed: {
        dateNextDisabled() {
            return !this.$moment(this.dateEnd).add(1, 'days').isSameOrBefore(this.$moment(), 'days');
        },
        device: function() {
            return this.$store.getters.selectedDevice;
        },
        dateEnd: {
            get() {
                return this.$store.state.map.historyEndDate;
            },
            set(value) {
                this.$store.commit('map/historyEndDate', value);
            }
        }
    },
    mounted() {
        this.clearState();
        this.$root.$on('getDeviceHistory', () => {
            this.getDeviceHistory();
        });
    },
    beforeDestroy() {
        this.clearState();
        this.clearDeviceHistory();
        this.$store.commit('map/reloadMarkers');
    }
};
</script>

<style lang="scss" scoped>
    .history {
        .dt-control-row {
            margin-top: 20px;
            display: flex;
            justify-content: space-between;
        }

        .field.datetime-field {
            width: 50%;
            display: inline-block;
            margin-top: 0.5rem;
        }
    }

    .history .field.datetime-field:first-child {
        padding-right: 10px;
    }
</style>

<style lang="scss">
.history {
    .message {
        margin-bottom: 0 !important;

        .message-body {
            padding: 0.4em;
            margin-top: 15px;
        }
    }

    h1 {
        text-align: center;
        margin-top: 1em;
        font-weight: 100;
        color: grey;
    }

    .field {
        .label {
            color: $off-white;
        }
    }

    .dropdown-content {
        padding-top: 0;
    }

    .dropdown:not(.is-mobile-modal) > .dropdown-menu {
        top: 0 !important;
        width: 100%;
    }

    @media screen and (max-width: 500px) {
         .dropdown.is-mobile-modal > .dropdown-menu {
            transform: translate3d(-50%, -40%, 0) !important;
        }
    }
    @media screen and (min-width: 1024px) {
         .dropdown.is-mobile-modal > .dropdown-menu {
            width: initial;
        }
    }

    @media screen and (max-width: 1023px) {
        .dropdown.is-mobile-modal > .dropdown-menu {
            width: 100% !important;
            top: 0 !important;
        }
    }
}
</style>
